// src/components/admin/TemplateManagement.js
import React, { useState, useEffect, useCallback } from "react";
import { PaginatedList } from "./PaginatedList";
import TemplateDesigner from "../TemplateDesigner";
import { Pencil, Trash2, FilePlus2 } from "lucide-react";

const TemplateManagement = ({
  setNotification,
  setActiveMenu,
  setConfirmModal,
}) => {
  // Add state variables that were previously in AdminDashboard
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    group: "",
    elements: {},
    preview: "",
  });

  // Fetch both templates and categories data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch categories and templates in parallel
      const [categoriesResponse, templatesResponse] = await Promise.all([
        fetch("/api/categories", {
          method: "GET",
          headers: headers,
        }),
        fetch("/api/templates", {
          method: "GET",
          headers: headers,
        }),
      ]);

      if (!categoriesResponse.ok) {
        throw new Error(`HTTP error! status: ${categoriesResponse.status}`);
      }

      if (!templatesResponse.ok) {
        throw new Error(`HTTP error! status: ${templatesResponse.status}`);
      }

      const categoriesData = await categoriesResponse.json();
      const templatesData = await templatesResponse.json();

      // Process templates to add full URLs for previews
      const processedTemplates = templatesData.map((template) => {
        // Create a processed template object
        const processedTemplate = { ...template };

        // Check if preview is a file path and add a full URL
        if (template.preview && typeof template.preview === "string") {
          if (template.preview.startsWith("/")) {
            processedTemplate.previewUrl = `/api${template.preview}`;
          }
          if (
            template.previewThumbnail &&
            template.previewThumbnail.startsWith("/")
          ) {
            processedTemplate.previewThumbnailUrl = `/api${template.previewThumbnail}`;
          }
        }

        return processedTemplate;
      });

      setCategories(categoriesData);
      setTemplates(processedTemplates);
    } catch (error) {
      console.error("Error fetching data:", error);
      setNotification({
        type: "error",
        message: "Failed to load data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [setNotification]);

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset form when templates change
  useEffect(() => {
    setShowEditForm(false);
    setFormData({
      name: "",
      category: "",
      group: "",
      elements: {},
      preview: "",
    });
  }, [templates.length]);

  // Function to switch to Add Template mode
  const handleAddTemplateClick = () => {
    setShowEditForm(true);
    setFormData({
      name: "",
      category: "",
      group: "",
      elements: {},
      preview: "",
    });
  };

  // Handle editing a template
  const handleEdit = (item) => {
    const editableItem = {
      ...item,
      _id: item._id,
      category: item.category?._id || item.category,
    };

    setSelectedItem(item);
    setFormData(editableItem);
    setShowEditForm(true);
  };

  // Handle template submission
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    try {
      // Log before submission to help debug
      console.log("Submitting template data:", formData);

      // Validation
      if (!formData.name || !formData.category) {
        setNotification({
          type: "error",
          message: "Template name and category are required",
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const templateData = {
        name: formData.name,
        category: formData.category,
        group: formData.group,
        elements: formData.elements || {},
        preview: formData.preview,
      };

      console.log("Template data being submitted:", templateData);

      let response;

      if (selectedItem && selectedItem._id) {
        console.log(`Updating template with ID: ${selectedItem._id}`);
        response = await fetch(`/api/templates/${selectedItem._id}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(templateData),
        });
      } else {
        console.log("Creating new template");
        response = await fetch("/api/templates", {
          method: "POST",
          headers,
          body: JSON.stringify(templateData),
        });
      }

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            `Failed to ${selectedItem ? "update" : "create"} template`
        );
      }

      await fetchData(); // Refresh data after submission
      setShowEditForm(false);

      setNotification({
        type: "success",
        message: formData._id
          ? "Template updated successfully!"
          : "Template added successfully!",
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error("Template submission error:", error);
      setNotification({
        type: "error",
        message:
          error.response?.data?.error ||
          `Failed to ${formData._id ? "update" : "add"} template`,
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle template deletion
  const handleDelete = (templateId) => {
    const template = templates.find((t) => t._id === templateId);

    setConfirmModal({
      show: true,
      title: "Delete Template",
      message: `Are you sure you want to delete "${template?.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`/api/templates/${templateId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || `Failed to delete template`);
          }

          await fetchData(); // Refresh data after deletion

          setNotification({
            type: "success",
            message: data.message || `Template deleted successfully!`,
          });

          setTimeout(() => setNotification(null), 3000);
        } catch (error) {
          console.error(`Error deleting template:`, error);

          setNotification({
            type: "error",
            message: error.message || `Failed to delete template`,
          });

          setTimeout(() => setNotification(null), 3000);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
        <img src="../images/loading.gif" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-semibold">Template Management</h3>

        {/* Add Template button */}
        {!showEditForm && (
          <button
            onClick={handleAddTemplateClick}
            className="bg-blue-500 rounded-md text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
          >
            <FilePlus2 className="h-5 w-5 mr-2" />
            Add Template
          </button>
        )}
      </div>

      {/* Show list of templates when not showing edit/add form */}
      {!showEditForm && (
        <PaginatedList
          items={templates}
          renderItem={(template) => (
            <div
              key={template._id}
              className="flex justify-between items-center p-4 bg-white rounded-lg shadow mb-4"
            >
              {template.preview && (
                <img
                  src={
                    template.previewUrl ||
                    (template.preview.startsWith("/")
                      ? `/api${template.preview}`
                      : template.preview)
                  }
                  alt={template.name}
                  className="w-24 h-32 object-contain mr-4"
                />
              )}
              <div className="flex-grow">
                <h4 className="font-semibold">{template.name}</h4>
                <p className="text-gray-600">
                  Category: {template.category?.name}
                </p>
              </div>
              <div className="flex space-x-2">
                {/* <button
                  onClick={() => handleEdit(template)}
                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                  title="Edit template"
                >
                  <Pencil className="h-5 w-5" />
                </button> */}
                <button
                  onClick={() => handleDelete(template._id)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                  title="Delete template"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        />
      )}

      {/* Show template designer when adding or editing */}
      {showEditForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">
              {formData._id ? "Edit" : "Add"} Template
            </h4>
            <button
              onClick={() => {
                setShowEditForm(false);
                setFormData({
                  name: "",
                  category: "",
                  elements: {},
                  preview: "",
                });
                setSelectedItem(null);
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>

          <TemplateDesigner
            onSave={(template) => {
              console.log("Template from designer:", template);
              setFormData(template);
              handleSubmit();
            }}
            initialTemplate={formData._id ? formData : null}
            categories={categories}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;

// // befoe img real in upload folder
// // src/components/admin/TemplateManagement.js
// import React, { useState, useEffect, useCallback } from "react";
// import { PaginatedList } from "./PaginatedList";
// import TemplateDesigner from "../TemplateDesigner";
// import { Pencil, Trash2, FilePlus2 } from "lucide-react";

// const TemplateManagement = ({
//   setNotification,
//   setActiveMenu,
//   setConfirmModal,
// }) => {
//   // Add state variables that were previously in AdminDashboard
//   const [templates, setTemplates] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showEditForm, setShowEditForm] = useState(false);
//   const [selectedItem, setSelectedItem] = useState(null);
//   const [formData, setFormData] = useState({
//     name: "",
//     category: "",
//     group: "",
//     elements: {},
//     preview: "",
//   });

//   // Fetch both templates and categories data
//   const fetchData = useCallback(async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem("token");
//       const headers = { Authorization: `Bearer ${token}` };

//       // Fetch categories and templates in parallel
//       const [categoriesResponse, templatesResponse] = await Promise.all([
//         fetch("/api/categories", {
//           method: "GET",
//           headers: headers,
//         }),
//         fetch("/api/templates", {
//           method: "GET",
//           headers: headers,
//         }),
//       ]);

//       if (!categoriesResponse.ok) {
//         throw new Error(`HTTP error! status: ${categoriesResponse.status}`);
//       }

//       if (!templatesResponse.ok) {
//         throw new Error(`HTTP error! status: ${templatesResponse.status}`);
//       }

//       const categoriesData = await categoriesResponse.json();
//       const templatesData = await templatesResponse.json();

//       setCategories(categoriesData);
//       setTemplates(templatesData);
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       setNotification({
//         type: "error",
//         message: "Failed to load data. Please try again.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   }, [setNotification]);

//   // Load data on component mount
//   useEffect(() => {
//     fetchData();
//   }, [fetchData]);

//   // Reset form when templates change
//   useEffect(() => {
//     setShowEditForm(false);
//     setFormData({
//       name: "",
//       category: "",
//       group: "",
//       elements: {},
//       preview: "",
//     });
//   }, [templates.length]);

//   // Function to switch to Add Template mode
//   const handleAddTemplateClick = () => {
//     setShowEditForm(true);
//     setFormData({
//       name: "",
//       category: "",
//       group: "",
//       elements: {},
//       preview: "",
//     });
//   };

//   // Handle editing a template
//   const handleEdit = (item) => {
//     const editableItem = {
//       ...item,
//       _id: item._id,
//       category: item.category?._id || item.category,
//     };

//     setSelectedItem(item);
//     setFormData(editableItem);
//     setShowEditForm(true);
//   };

//   // Handle template submission
//   const handleSubmit = async (e) => {
//     if (e && e.preventDefault) {
//       e.preventDefault();
//     }

//     try {
//       // Log before submission to help debug
//       console.log("Submitting template data:", formData);

//       // Validation
//       if (!formData.name || !formData.category) {
//         setNotification({
//           type: "error",
//           message: "Template name and category are required",
//         });
//         setTimeout(() => setNotification(null), 3000);
//         return;
//       }

//       const token = localStorage.getItem("token");
//       const headers = {
//         Authorization: `Bearer ${token}`,
//         "Content-Type": "application/json",
//       };

//       const templateData = {
//         name: formData.name,
//         category: formData.category,
//         group: formData.group,
//         elements: formData.elements || {},
//         preview: formData.preview,
//       };

//       console.log("Template data being submitted:", templateData);

//       let response;

//       if (selectedItem && selectedItem._id) {
//         console.log(`Updating template with ID: ${selectedItem._id}`);
//         response = await fetch(`/api/templates/${selectedItem._id}`, {
//           method: "PUT",
//           headers,
//           body: JSON.stringify(templateData),
//         });
//       } else {
//         console.log("Creating new template");
//         response = await fetch("/api/templates", {
//           method: "POST",
//           headers,
//           body: JSON.stringify(templateData),
//         });
//       }

//       const responseData = await response.json();

//       if (!response.ok) {
//         throw new Error(
//           responseData.error ||
//             `Failed to ${selectedItem ? "update" : "create"} template`
//         );
//       }

//       await fetchData(); // Refresh data after submission
//       setShowEditForm(false);

//       setNotification({
//         type: "success",
//         message: formData._id
//           ? "Template updated successfully!"
//           : "Template added successfully!",
//       });
//       setTimeout(() => setNotification(null), 3000);
//     } catch (error) {
//       console.error("Template submission error:", error);
//       setNotification({
//         type: "error",
//         message:
//           error.response?.data?.error ||
//           `Failed to ${formData._id ? "update" : "add"} template`,
//       });
//       setTimeout(() => setNotification(null), 3000);
//     }
//   };

//   // Handle template deletion
//   const handleDelete = (templateId) => {
//     const template = templates.find((t) => t._id === templateId);

//     setConfirmModal({
//       show: true,
//       title: "Delete Template",
//       message: `Are you sure you want to delete "${template?.name}"? This action cannot be undone.`,
//       onConfirm: async () => {
//         try {
//           const token = localStorage.getItem("token");
//           const response = await fetch(`/api/templates/${templateId}`, {
//             method: "DELETE",
//             headers: { Authorization: `Bearer ${token}` },
//           });

//           const data = await response.json();

//           if (!response.ok) {
//             throw new Error(data.error || `Failed to delete template`);
//           }

//           await fetchData(); // Refresh data after deletion

//           setNotification({
//             type: "success",
//             message: data.message || `Template deleted successfully!`,
//           });

//           setTimeout(() => setNotification(null), 3000);
//         } catch (error) {
//           console.error(`Error deleting template:`, error);

//           setNotification({
//             type: "error",
//             message: error.message || `Failed to delete template`,
//           });

//           setTimeout(() => setNotification(null), 3000);
//         }
//       },
//     });
//   };

//   if (loading) {
//     return (
//       <div className="fixed inset-0 flex flex-col items-center justify-center">
//         {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
//         <img src="../images/loading.gif" />
//       </div>
//     );
//   }

//   return (
//     <div>
//       <div className="flex justify-between items-center mb-6">
//         <h3 className="text-2xl font-semibold">Template Management</h3>

//         {/* Add Template button */}
//         {!showEditForm && (
//           <button
//             onClick={handleAddTemplateClick}
//             className="bg-blue-500 rounded-md text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
//           >
//             <FilePlus2 className="h-5 w-5 mr-2" />
//             Add Template
//           </button>
//         )}
//       </div>

//       {/* Show list of templates when not showing edit/add form */}
//       {!showEditForm && (
//         <PaginatedList
//           items={templates}
//           renderItem={(template) => (
//             <div
//               key={template._id}
//               className="flex justify-between items-center p-4 bg-white rounded-lg shadow mb-4"
//             >
//               {template.preview && (
//                 <img
//                   src={template.preview}
//                   alt={template.name}
//                   className="w-24 h-32 object-contain mr-4"
//                 />
//               )}
//               <div className="flex-grow">
//                 <h4 className="font-semibold">{template.name}</h4>
//                 <p className="text-gray-600">
//                   Category: {template.category?.name}
//                 </p>
//               </div>
//               <div className="flex space-x-2">
//                 {/* <button
//                   onClick={() => handleEdit(template)}
//                   className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
//                   title="Edit template"
//                 >
//                   <Pencil className="h-5 w-5" />
//                 </button> */}
//                 <button
//                   onClick={() => handleDelete(template._id)}
//                   className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
//                   title="Delete template"
//                 >
//                   <Trash2 className="h-5 w-5" />
//                 </button>
//               </div>
//             </div>
//           )}
//         />
//       )}

//       {/* Show template designer when adding or editing */}
//       {showEditForm && (
//         <div className="bg-white p-6 rounded-lg shadow">
//           <div className="flex justify-between items-center mb-4">
//             <h4 className="text-lg font-medium">
//               {formData._id ? "Edit" : "Add"} Template
//             </h4>
//             <button
//               onClick={() => {
//                 setShowEditForm(false);
//                 setFormData({
//                   name: "",
//                   category: "",
//                   elements: {},
//                   preview: "",
//                 });
//                 setSelectedItem(null);
//               }}
//               className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
//             >
//               Cancel
//             </button>
//           </div>

//           <TemplateDesigner
//             onSave={(template) => {
//               console.log("Template from designer:", template);
//               setFormData(template);
//               handleSubmit();
//             }}
//             initialTemplate={formData._id ? formData : null}
//             categories={categories}
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// export default TemplateManagement;
